import './RunReview.scss';
import { FilterColumn } from '@blisspointmedia/bpm-types/dist/FilterBar';
import cn from 'classnames';
import { KeyboardEvent, memo, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Toast, ToastContainer } from 'react-bootstrap';
import { MdCheck, MdClose, MdExpandMore, MdWarning } from 'react-icons/md';
import { useParams, useSearchParams } from 'react-router-dom';
import { capitalizeString } from '@blisspointmedia/bpm-types/dist/StyleUtils';
import {
  ApprovalStatus,
  CopyTigerAdApproval,
  CopyTigerLineField,
  CopyTigerLineRejection,
  CopyTigerRunApprovals,
  CopyTigerStagedLineRejection,
  ReviewStage
} from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { Badge } from '../../Components/Badge';
import { Button } from '../../Components/Button';
import {
  Appearance,
  Design,
  DesignType,
  Intent,
  IntentType,
  Size
} from '../../Components/designConstants';
import { AlertDialog } from '../../Components/AlertDialog';
import { EmptyState } from '../../Components/EmptyState';
import { IconButton } from '../../Components/IconButton';
import { InfoTooltip } from '../../Components/InfoTooltip';
import { Checkbox } from '../../Components/FormCheck';
import { FilterBar } from '../../Components/FilterBar';
import { Spinner } from '../../Components/Spinner';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { dateToLongDate, formatFriendlyTimestamp } from '../../utils/date-utils';
import { FetchError } from '../../utils/fetch-error';
import { getSafeErrorMessage } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useStateFunction } from '../../utils/hooks/useData';
import { getPlatformLabel, isAdPlatform } from './copyTigerUtils';
import { MOCK_APPROVALS, MOCK_RUN, MOCK_SENT_APPROVALS, MOCK_SUBMITTED_APPROVALS } from './mockRun';
import { ReadyToApply } from './ReadyToApply';
import {
  adKey,
  approvalKey,
  CopyMark,
  CopyTigerAdDecision,
  CopyTigerRun,
  CopyTigerRunAd,
  getAdKeywords,
  getCopyTigerRun,
  getCopyTigerRunApprovals,
  getRunAdGroups,
  markCopy,
  MarkedLine,
  RunAdGroup,
  sendCopyTigerRunToClient,
  submitCopyTigerClientReview,
  setCopyTigerAdApprovals,
  isViewingAsClient,
  formatFlagReason,
  STATUS_INTENT
} from './runReviewUtils';

const FILTER_OPTIONS: FilterColumn[] = [
  { label: 'Platform', name: 'platform' },
  { label: 'Account', name: 'account' },
  { label: 'Campaign', name: 'campaign' },
  { label: 'Ad Group', name: 'adGroup' },
  { label: 'Status', name: 'status' }
];

interface AdFilterLine {
  key: string;
  platform: string;
  account: string;
  campaign: string;
  adGroup: string;
  status: ApprovalStatus;
}

// An ad group within an account, from an adKey (platform|accountId|adGroupId|adId).
const groupIdOf = (key: string) => key.split('|').slice(0, 3).join('|');

// Makes a card's header row toggle the card on click, Enter or Space. Clicks on controls inside the header
// (checkboxes, Approve/Reject, the note field) keep their own behavior.
// Keywords an ad shows before the rest fold behind '+N more' (about two rows in the ad header).
const KEYWORD_LIMIT = 8;

const INTERACTIVE_SELECTOR = 'button, input, label, a, textarea, select';
const toggleOnClickProps = (onToggle: () => void, isExpanded: boolean) => ({
  role: 'button',
  tabIndex: 0,
  'aria-expanded': isExpanded,
  onClick: (e: MouseEvent<HTMLElement>) => {
    if (!(e.target instanceof Element && e.target.closest(INTERACTIVE_SELECTOR))) {
      onToggle();
    }
  },
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggle();
    }
  }
});

// ?mock=<state> fixtures (dev builds only): the synthetic run fresh, sent to the client, and submitted by
// the client.
const MOCK_STATES: Partial<
  Record<string, { run: CopyTigerRun; approvals: CopyTigerRunApprovals }>
> = {
  '1': { run: MOCK_RUN, approvals: MOCK_APPROVALS },
  sent: { run: MOCK_RUN, approvals: MOCK_SENT_APPROVALS },
  submitted: { run: MOCK_RUN, approvals: MOCK_SUBMITTED_APPROVALS }
};

const PENDING_APPROVAL: Omit<CopyTigerAdApproval, 'platform' | 'accountId' | 'adGroupId' | 'adId'> =
  {
    internalStatus: 'pending',
    internalReviewer: null,
    internalReviewedAt: null,
    internalNote: null,
    clientStatus: 'pending',
    clientReviewer: null,
    clientReviewedAt: null,
    clientNote: null,
    lineRejections: [],
    modifiedAt: null
  };

const MARK_DESIGN: Record<CopyMark, DesignType | IntentType> = {
  kept: Design.NEUTRAL,
  new: Design.BRAND,
  removed: Intent.ERROR
};

// Line-level review of the generated column: the viewer's own rejections can be toggled and commented
// while the ad is editable; the other stage's rejections are struck through (internal) or hidden (client).
interface LineReview {
  stage: ReviewStage;
  rejections: CopyTigerStagedLineRejection[];
  // Internal rejections a client must not see (only the unpruned mock has any).
  hiddenRejections: CopyTigerLineRejection[];
  isEditable: boolean;
}

// `save` marks a finished change (a toggle, or leaving a comment box) rather than a keystroke.
type OnLinesChange = (rejections: CopyTigerLineRejection[], save?: boolean) => void;

const CopyList = ({
  label,
  lines,
  field,
  review,
  onLinesChange
}: {
  label: string;
  lines: MarkedLine[];
  field?: CopyTigerLineField;
  review?: LineReview;
  onLinesChange?: OnLinesChange;
}) => {
  const findRejection = (index: number) =>
    review?.rejections.find(r => r.field === field && r.index === index);
  const own = review?.rejections.filter(r => r.stage === review.stage) ?? [];
  const visible = lines.map((line, index) => ({ line, index, rejection: findRejection(index) }));
  const setOwn = (rejections: CopyTigerLineRejection[], save?: boolean) =>
    onLinesChange?.(
      rejections.map(({ field: f, index, text, note }) => ({ field: f, index, text, note })),
      save
    );
  return (
    <div className='run-review-copy-list'>
      <div className='run-review-copy-label'>
        {label} ({visible.length})
      </div>
      {/* Generated headlines are short, so they sit in two columns. */}
      <ul
        className={cn('run-review-copy-lines', { 'two-column': review && field === 'headlines' })}
      >
        {visible.map(({ line, index, rejection }) => {
          const isOwn = rejection?.stage === review?.stage;
          return (
            <li
              key={index}
              className={cn('run-review-copy-line', line.mark, { rejected: rejection })}
            >
              <Badge
                className='run-review-copy-mark'
                size={Size.SM}
                design={MARK_DESIGN[line.mark]}
              >
                {capitalizeString(line.mark)}
              </Badge>
              <span className='run-review-copy-text'>{line.text}</span>
              {review?.isEditable && field && (!rejection || isOwn) && (
                <IconButton
                  size={Size.SM}
                  design={Intent.ERROR}
                  appearance={rejection ? Appearance.FILLED : Appearance.EMPTY}
                  tooltip={{ text: rejection ? 'Undo line rejection' : 'Reject this line' }}
                  onClick={() =>
                    setOwn(
                      rejection
                        ? own.filter(r => r !== rejection)
                        : [...own, { field, index, text: line.text }],
                      true
                    )
                  }
                >
                  <MdClose />
                </IconButton>
              )}
              {rejection && review?.isEditable && isOwn ? (
                <Form.Control
                  className='run-review-line-note-field'
                  size='sm'
                  placeholder='Why? (optional)'
                  value={rejection.note ?? ''}
                  onChange={e =>
                    setOwn(own.map(r => (r === rejection ? { ...r, note: e.target.value } : r)))
                  }
                  onBlur={() => setOwn(own, true)}
                />
              ) : (
                rejection && (
                  <span className='run-review-ad-note'>
                    {`${isOwn ? 'Rejected' : 'Client rejected'}${rejection.note ? `: ${rejection.note}` : ''}`}
                  </span>
                )
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// Clients never see lines the internal team rejected, not even as removed, so their diff runs against the
// pruned generated copy. The real client run already arrives pruned (with client rejections indexed into
// it); pruning here keeps the unpruned mock the same.
const getShownCopy = (ad: CopyTigerRunAd, field: CopyTigerLineField, review: LineReview) =>
  review.stage === 'client'
    ? ad.generated[field].filter(
        (_, index) => !review.hiddenRejections.some(r => r.field === field && r.index === index)
      )
    : ad.generated[field];

// "+N new / −M removed" for an ad's header, counted from the same marks its copy columns show.
const getDiffSummary = (ad: CopyTigerRunAd, review: LineReview) => {
  const marked = (['headlines', 'descriptions'] as const).map(field =>
    markCopy(ad.current[field], getShownCopy(ad, field, review))
  );
  const added = marked.flatMap(m => m.generated).filter(line => line.mark === 'new').length;
  const removed = marked.flatMap(m => m.current).filter(line => line.mark === 'removed').length;
  return `+${added} new / −${removed} removed`;
};

const AdCopyContent = ({
  ad,
  review,
  onLinesChange
}: {
  ad: CopyTigerRunAd;
  review: LineReview;
  onLinesChange: OnLinesChange;
}) => {
  const headlines = markCopy(ad.current.headlines, getShownCopy(ad, 'headlines', review));
  const descriptions = markCopy(ad.current.descriptions, getShownCopy(ad, 'descriptions', review));
  return (
    <div className='run-review-copy diff'>
      <div className='run-review-copy-side'>
        <h4>Current</h4>
        <CopyList label='Headlines' lines={headlines.current} />
        <CopyList label='Descriptions' lines={descriptions.current} />
      </div>
      <div className='run-review-copy-side'>
        <h4>Generated</h4>
        <CopyList
          label='Headlines'
          lines={headlines.generated}
          field='headlines'
          review={review}
          onLinesChange={onLinesChange}
        />
        <CopyList
          label='Descriptions'
          lines={descriptions.generated}
          field='descriptions'
          review={review}
          onLinesChange={onLinesChange}
        />
      </div>
    </div>
  );
};

// With hundreds of ads, re-rendering every copy diff made each selection click take about a second.
// The copy only changes with the ad or its line review, so other state changes skip it. onLinesChange
// is left out of the comparison: the page passes one that always calls its latest handler.
const AdCopy = memo(
  AdCopyContent,
  (prev, next) =>
    prev.ad === next.ad &&
    prev.review.stage === next.review.stage &&
    prev.review.isEditable === next.review.isEditable &&
    JSON.stringify([prev.review.rejections, prev.review.hiddenRejections]) ===
      JSON.stringify([next.review.rejections, next.review.hiddenRejections])
);

export const RunReview = () => {
  const { runId = '' } = useParams();
  const [searchParams] = useSearchParams();
  // Mock mode renders the synthetic fixture and keeps every action in local state, so the page works
  // before the copytiger run endpoints are deployed.
  // ?mock=sent loads the same run already sent to the client. Dev builds (and Storybook) only.
  const mockParam = import.meta.env.DEV ? searchParams.get('mock') : null;
  const mockState = mockParam ? MOCK_STATES[mockParam] : undefined;
  const isMock = mockState != null;
  const { cid: company } = useCompanyInfo();
  const isViewAsClient = isViewingAsClient(searchParams.toString());
  const isInternal = useAppSelector(UserRedux.isInternalSelector) && !isViewAsClient;
  const email = useAppSelector(UserRedux.emailSelector);

  const [run, setRun] = useState<CopyTigerRun>();
  const [approvals, setApprovals] = useState<CopyTigerRunApprovals>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adFilter, setAdFilter] = useStateFunction<(line: AdFilterLine) => boolean>(() => true);
  // Per-ad note edits, keyed by ad. Unedited ads show their saved note for the current stage.
  const [adNotes, setAdNotes] = useState<Record<string, string>>({});
  // Unsaved line rejections for the viewer's stage, per ad; saved with the ad's decision.
  const [lineDrafts, setLineDrafts] = useState<Record<string, CopyTigerLineRejection[]>>({});
  // Saves apply locally first and run in the background. Each ad's newest save and its last confirmed
  // row (undefined when it had none) live here, so a stale response or a failure never overwrites a
  // newer click.
  const unsavedAds = useRef(new Map<string, { saveId: number; confirmed?: CopyTigerAdApproval }>());
  const lastSaveId = useRef(0);
  // Saves go out one at a time in click order, so the server ends on the last click too.
  const saveChain = useRef<Promise<unknown>>(Promise.resolve());
  const [savingCount, setSavingCount] = useState(0);
  // The memoized ad copy calls this, so a skipped re-render never leaves it on stale page state.
  const linesChange =
    useRef<(key: string, rejections: CopyTigerLineRejection[], save?: boolean) => void>(undefined);
  const [saveError, setSaveError] = useState<string>();
  // Decided ads start collapsed and pending ones open; this holds the ads toggled away from that by hand.
  const [toggledAds, setToggledAds] = useState<Set<string>>(new Set());
  // Ads whose full keyword list is shown.
  const [expandedKeywords, setExpandedKeywords] = useState<Set<string>>(new Set());
  // Fully decided groups start collapsed and the rest open; this holds the groups toggled by hand.
  const [toggledGroups, setToggledGroups] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitConfirmShown, setIsSubmitConfirmShown] = useState(false);
  const [isSentToGoogle, setIsSentToGoogle] = useState(false);
  // Clients get a 403 until the run has been sent to them.
  const [isNotReady, setIsNotReady] = useState(false);
  const [loadError, setLoadError] = useState<string>();
  const setError = useSetError();

  useEffect(() => {
    if (isMock) {
      setRun(mockState.run);
      setApprovals(mockState.approvals);
      return;
    }
    if (!company || !runId) {
      return;
    }
    (async () => {
      try {
        const [runData, approvalData] = await Promise.all([
          getCopyTigerRun(company, runId),
          getCopyTigerRunApprovals(company, runId)
        ]);
        setRun(runData);
        setApprovals(approvalData);
      } catch (e) {
        if (!isInternal && e instanceof FetchError && e.status === 403) {
          setIsNotReady(true);
          return;
        }
        setLoadError(getSafeErrorMessage(e));
      }
    })();
  }, [company, isInternal, isMock, mockState, runId]);

  const approvalsByKey = useMemo(
    () => Object.fromEntries((approvals?.approvals ?? []).map(a => [approvalKey(a), a])),
    [approvals]
  );
  const getApproval = (key: string) => approvalsByKey[key] ?? PENDING_APPROVAL;
  // Saving a decision without a note clears the saved one, so the field starts from the saved note.
  const getAdNote = (key: string) =>
    adNotes[key] ??
    (isInternal ? getApproval(key).internalNote : getApproval(key).clientNote) ??
    '';

  const stage: ReviewStage = isInternal ? 'internal' : 'client';
  const getLineRejections = (key: string): CopyTigerLineRejection[] =>
    lineDrafts[key] ??
    getApproval(key)
      .lineRejections.filter(r => r.stage === stage)
      .map(({ field, index, text, note }) => ({ field, index, text, note }));
  // The viewer's own rejections (drafts included), plus the client's for internal users. Clients never get
  // internal rejections.
  const getVisibleLineRejections = (key: string): CopyTigerStagedLineRejection[] => [
    ...(isInternal ? getApproval(key).lineRejections.filter(r => r.stage === 'client') : []),
    ...getLineRejections(key).map(r => ({ ...r, stage }))
  ];

  const runAdGroups = useMemo(() => (run ? getRunAdGroups(run) : []), [run]);
  const internalPendingCount = runAdGroups
    .flatMap(group =>
      group.ads.map(ad => adKey(group.platform, group.accountId, group.adGroupId, ad.adId))
    )
    .filter(key => (approvalsByKey[key] ?? PENDING_APPROVAL).internalStatus === 'pending').length;

  // One FilterBar line per ad the viewer can see; clients only see ads that passed internal review.
  // Names come from the run's context when present, else ids. Status is the viewer's own stage.
  const filterLines = useMemo<AdFilterLine[]>(
    () =>
      runAdGroups.flatMap(group =>
        group.ads.flatMap(ad => {
          const key = adKey(group.platform, group.accountId, group.adGroupId, ad.adId);
          const approval = approvalsByKey[key] ?? PENDING_APPROVAL;
          if (!isInternal && approval.internalStatus !== 'approved') {
            return [];
          }
          return [
            {
              key,
              platform: getPlatformLabel(group.platform),
              account: group.accountId,
              campaign: group.campaignName ?? group.campaignId,
              adGroup: group.adGroupName ?? group.adGroupId,
              status: isInternal ? approval.internalStatus : approval.clientStatus
            }
          ];
        })
      ),
    [approvalsByKey, isInternal, runAdGroups]
  );

  const adGroups = useMemo<RunAdGroup[]>(() => {
    const visibleKeys = new Set(filterLines.filter(adFilter).map(line => line.key));
    return runAdGroups
      .map(group => ({
        ...group,
        ads: group.ads.filter(ad =>
          visibleKeys.has(adKey(group.platform, group.accountId, group.adGroupId, ad.adId))
        )
      }))
      .filter(group => group.ads.length > 0);
  }, [adFilter, filterLines, runAdGroups]);

  // A client's decision is final, so ads the client has decided are read-only for everyone.
  // Once the run is sent, internal decisions are final too (the backend rejects them).
  const isSentForInternal = isInternal && !!approvals?.run.sentToClientAt;
  // After the client submits, their review is final too.
  const isSubmitted = !!approvals?.run.clientSubmittedAt;
  const isReadOnly = (key: string) =>
    isSentForInternal || isSubmitted || getApproval(key).clientStatus !== 'pending';
  const stageStatus = (key: string) =>
    isInternal ? getApproval(key).internalStatus : getApproval(key).clientStatus;
  const toggleExpanded = (setter: typeof setToggledAds, id: string) =>
    setter(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  const groupKeys = (group: RunAdGroup) =>
    group.ads
      .map(ad => adKey(group.platform, group.accountId, group.adGroupId, ad.adId))
      .filter(key => !isReadOnly(key));
  const allKeys = adGroups.flatMap(groupKeys);
  // Only visible ads count, so hidden selections from another filter aren't acted on.
  const selectedKeys = allKeys.filter(key => selected.has(key));
  const selectedCount = selectedKeys.length;

  const toggle = (keys: string[], checked: boolean) =>
    setSelected(current => {
      const next = new Set(current);
      keys.forEach(key => (checked ? next.add(key) : next.delete(key)));
      return next;
    });

  const selectAllProps = (keys: string[]) => {
    const count = keys.filter(key => selected.has(key)).length;
    return {
      checked: keys.length > 0 && count === keys.length,
      indeterminate: count > 0 && count < keys.length,
      onChange: () => toggle(keys, count < keys.length)
    };
  };

  // Applies decisions to local state as the viewer's stage, before (or, in mock mode, instead of) saving.
  const applyLocalDecisions = (decisions: CopyTigerAdDecision[]) => {
    const reviewedAt = new Date().toISOString();
    setApprovals(current => {
      if (!current) {
        return current;
      }
      const byKey = new Map(current.approvals.map(a => [approvalKey(a), a]));
      decisions.forEach(
        ({
          platform,
          accountId,
          adGroupId = '',
          adId,
          status,
          note: decisionNote,
          lineRejections = []
        }) => {
          const key = adKey(platform, accountId, adGroupId, adId);
          const base: CopyTigerAdApproval = byKey.get(key) ?? {
            ...PENDING_APPROVAL,
            platform,
            accountId,
            adGroupId,
            adId
          };
          const withLines = {
            ...base,
            lineRejections: [
              ...base.lineRejections.filter(r => r.stage !== stage),
              ...lineRejections.map(r => ({ ...r, stage }))
            ]
          };
          byKey.set(
            key,
            isInternal
              ? {
                  ...withLines,
                  internalStatus: status,
                  internalReviewer: email,
                  internalReviewedAt: reviewedAt,
                  internalNote: decisionNote ?? null
                }
              : {
                  ...withLines,
                  clientStatus: status,
                  clientReviewer: email,
                  clientReviewedAt: reviewedAt,
                  clientNote: decisionNote ?? null
                }
          );
        }
      );
      return { ...current, approvals: [...byKey.values()] };
    });
  };

  const toDecision = (
    key: string,
    status: CopyTigerAdDecision['status'],
    lineRejections = getLineRejections(key)
  ): CopyTigerAdDecision | undefined => {
    const [platform, accountId, adGroupId, adId] = key.split('|');
    // Keys come from the run, so this only skips a malformed one rather than sending a bad platform.
    if (!isAdPlatform(platform)) {
      return undefined;
    }
    // Each ad's note box is saved as-is; an empty box clears the saved note.
    return {
      platform,
      accountId,
      adGroupId,
      adId,
      status,
      note: getAdNote(key) || undefined,
      lineRejections
    };
  };

  // Applies the decisions now and saves them in the background. Resolves true once the server has
  // them; on failure the ads go back to their last saved state and an error toast shows.
  const saveDecisions = async (decisions: CopyTigerAdDecision[]) => {
    const keys = decisions.map(d => adKey(d.platform, d.accountId, d.adGroupId ?? '', d.adId));
    const saveId = ++lastSaveId.current;
    keys.forEach(key => {
      const confirmed = unsavedAds.current.has(key)
        ? unsavedAds.current.get(key)?.confirmed
        : approvalsByKey[key];
      unsavedAds.current.set(key, { saveId, confirmed });
    });
    applyLocalDecisions(decisions);
    if (isMock) {
      keys.forEach(key => unsavedAds.current.delete(key));
      return true;
    }
    const isLatest = (key: string) => unsavedAds.current.get(key)?.saveId === saveId;
    setSavingCount(count => count + 1);
    try {
      const request = saveChain.current.then(() =>
        setCopyTigerAdApprovals(company, runId, decisions)
      );
      saveChain.current = request.catch(() => undefined);
      const saved = await request;
      // A newer click on an ad keeps its local state; this row only becomes its rollback point.
      const fresh = saved.filter(row => {
        const entry = unsavedAds.current.get(approvalKey(row));
        if (entry && entry.saveId !== saveId) {
          entry.confirmed = row;
          return false;
        }
        return true;
      });
      const freshKeys = new Set(fresh.map(approvalKey));
      freshKeys.forEach(key => unsavedAds.current.delete(key));
      setApprovals(
        current =>
          current && {
            ...current,
            approvals: [...current.approvals.filter(a => !freshKeys.has(approvalKey(a))), ...fresh]
          }
      );
      return true;
    } catch (e) {
      const rollback = new Map(
        keys.filter(isLatest).map(key => [key, unsavedAds.current.get(key)?.confirmed])
      );
      rollback.forEach((_, key) => unsavedAds.current.delete(key));
      setApprovals(
        current =>
          current && {
            ...current,
            approvals: [
              ...current.approvals.filter(a => !rollback.has(approvalKey(a))),
              ...[...rollback.values()].filter(a => a != null)
            ]
          }
      );
      setSaveError(getSafeErrorMessage(e));
      return false;
    } finally {
      setSavingCount(count => count - 1);
    }
  };

  const decide = async (status: CopyTigerAdDecision['status'], keys: string[]) => {
    // Let the newly decided ads (and any group they complete) collapse right away.
    const decided = new Set(keys);
    const decidedGroups = new Set(keys.map(groupIdOf));
    setToggledAds(current => new Set([...current].filter(key => !decided.has(key))));
    setToggledGroups(current => new Set([...current].filter(id => !decidedGroups.has(id))));
    if (await saveDecisions(keys.flatMap(key => toDecision(key, status) ?? []))) {
      // Clear only what was saved, so other ads keep their selection and unsaved notes, and a failed
      // save keeps what the reviewer typed.
      setSelected(current => new Set([...current].filter(key => !decided.has(key))));
      setAdNotes(current =>
        Object.fromEntries(Object.entries(current).filter(([key]) => !decided.has(key)))
      );
      setLineDrafts(current =>
        Object.fromEntries(Object.entries(current).filter(([key]) => !decided.has(key)))
      );
    }
  };

  // Line changes on an ad already decided for the viewer's stage save on their own, re-sending its
  // current status; on a pending ad they wait for Approve/Reject. The ad stays open while editing.
  const saveLines = async (key: string, rejections: CopyTigerLineRejection[]) => {
    const status = stageStatus(key);
    const toSaved = (list: CopyTigerLineRejection[]) =>
      JSON.stringify(list.map(({ field, index, note }) => [field, index, note || null]));
    const savedOwn = getApproval(key).lineRejections.filter(r => r.stage === stage);
    if (status === 'pending' || toSaved(rejections) === toSaved(savedOwn)) {
      return;
    }
    const decision = toDecision(key, status, rejections);
    if (decision && (await saveDecisions([decision]))) {
      // Keep any newer draft made while this save was in flight.
      setLineDrafts(current =>
        current[key] === rejections
          ? Object.fromEntries(Object.entries(current).filter(([k]) => k !== key))
          : current
      );
    }
  };

  useEffect(() => {
    linesChange.current = (key, rejections, save) => {
      setLineDrafts(current => ({ ...current, [key]: rejections }));
      if (save) {
        saveLines(key, rejections);
      }
    };
  });

  const sendToClient = async () => {
    setIsSending(true);
    try {
      if (isMock) {
        setApprovals(
          current =>
            current && {
              ...current,
              run: {
                ...current.run,
                stage: 'client_review',
                sentToClientAt: new Date().toISOString(),
                sentBy: email
              }
            }
        );
      } else {
        await sendCopyTigerRunToClient(company, runId);
        setApprovals(await getCopyTigerRunApprovals(company, runId));
      }
    } catch (e) {
      setError({
        title: "Couldn't send to client",
        message: getSafeErrorMessage(e),
        reportError: e
      });
    } finally {
      setIsSending(false);
    }
  };

  const submitReview = async () => {
    setIsSubmitting(true);
    try {
      if (isMock) {
        setApprovals(
          current =>
            current && {
              ...current,
              run: {
                ...current.run,
                stage: 'ready_to_apply',
                clientSubmittedAt: new Date().toISOString(),
                clientSubmittedBy: email
              }
            }
        );
      } else {
        await submitCopyTigerClientReview(company, runId);
        setApprovals(await getCopyTigerRunApprovals(company, runId));
      }
    } catch (e) {
      setError({
        title: "Couldn't submit review",
        message: getSafeErrorMessage(e),
        reportError: e
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isNotReady) {
    return (
      <EmptyState
        title="This run isn't ready for review yet"
        subtitle='You can review it once Tinuiti sends it to you.'
      />
    );
  }

  if (loadError) {
    return <div className='run-review-error'>{`Couldn't load this run: ${loadError}`}</div>;
  }

  if (!run || !approvals) {
    return (
      <div className='run-review-loading'>
        <Spinner />
      </div>
    );
  }

  const sentAt = approvals.run.sentToClientAt;
  const submittedAt = approvals.run.clientSubmittedAt;
  const clientPendingCount = filterLines.filter(line => line.status === 'pending').length;

  return (
    <div className='run-review'>
      <div className='run-review-title-bar'>
        <div className='run-review-title-text'>
          <div className='run-review-title'>
            {`${dateToLongDate(run.createdAt)} Suggestions`}
            {isViewAsClient && (
              <Badge size={Size.SM} design={Design.BRAND}>
                Viewing as client (dev)
              </Badge>
            )}
          </div>
          <div className='run-review-subtitle'>
            {`${isInternal ? run.summary.adCount : filterLines.length} ads · ${run.trigger === 'scheduled' ? 'Scheduled' : 'On demand'}`}
          </div>
        </div>
        {savingCount > 0 && (
          <div className='run-review-saving'>
            <Spinner size={14} />
            Saving…
          </div>
        )}
        {isInternal && !sentAt && (
          <Button
            size={Size.LG}
            disabled={internalPendingCount > 0 || isSending || savingCount > 0}
            tooltip={
              internalPendingCount > 0
                ? { text: 'Approve or reject every ad before sending the run to the client.' }
                : undefined
            }
            loading={isSending}
            onClick={sendToClient}
          >
            {internalPendingCount > 0
              ? `Send to client (${internalPendingCount} pending)`
              : 'Send to client'}
          </Button>
        )}
        {!isInternal && !submittedAt && (
          <Button
            size={Size.LG}
            disabled={clientPendingCount > 0 || isSubmitting || savingCount > 0}
            tooltip={
              clientPendingCount > 0
                ? { text: 'Approve or reject every ad before submitting your review.' }
                : undefined
            }
            loading={isSubmitting}
            onClick={() => setIsSubmitConfirmShown(true)}
          >
            {clientPendingCount > 0
              ? `Submit review (${clientPendingCount} pending)`
              : 'Submit review'}
          </Button>
        )}
        {isInternal && submittedAt && (
          // TODO: call the real apply endpoint. Demo only: this flips local state and sends nothing.
          <Button
            size={Size.LG}
            disabled={isSentToGoogle}
            leadingElement={isSentToGoogle ? <MdCheck /> : undefined}
            onClick={() => setIsSentToGoogle(true)}
          >
            {isSentToGoogle ? 'Sent to Google (demo)' : 'Send to Google'}
          </Button>
        )}
      </div>
      {isInternal && sentAt && !submittedAt && (
        <Alert variant='info'>
          {`Sent to client ${formatFriendlyTimestamp(sentAt)}. Internal decisions are final; client progress shows on each ad.`}
        </Alert>
      )}
      {/* Internal users see the submit time in the Ready to send summary instead. */}
      {!isInternal && submittedAt && (
        <Alert variant='info'>{`Submitted ${formatFriendlyTimestamp(submittedAt)}`}</Alert>
      )}
      {isInternal && isSentToGoogle && (
        <Alert variant='success'>
          Sent to Google (demo). Nothing was sent: applying to the platforms is not built yet.
        </Alert>
      )}
      <AlertDialog
        show={isSubmitConfirmShown}
        onHide={() => setIsSubmitConfirmShown(false)}
        title='Submit your review?'
        body="You won't be able to change your decisions or line rejections after submitting."
        primaryAction={{ onClick: submitReview, label: 'Submit review' }}
        secondaryAction={{ onClick: () => {} }}
      />
      {isInternal && submittedAt ? (
        <ReadyToApply adGroups={runAdGroups} getApproval={getApproval} run={approvals.run} />
      ) : (
        <>
          <div className='run-review-header'>
            <FilterBar
              className='run-review-filter'
              size={Size.LG}
              hasAdvanced
              options={FILTER_OPTIONS}
              lines={filterLines}
              onFilter={setAdFilter}
            />
          </div>
          {/* List header (table/Gmail style): its checkbox lines up with the ad-group checkboxes below.
          Hidden when nothing visible is actionable (a sent run for internal, or all decided). */}
          {allKeys.length > 0 && (
            <div className='run-review-list-header'>
              <Checkbox
                id='run-review-select-all'
                label={`Select all (${allKeys.length})`}
                {...selectAllProps(allKeys)}
              />
              <div className='run-review-list-actions'>
                <span className='run-review-selected-count'>{`${selectedCount} selected`}</span>
                <Button
                  design={Intent.SUCCESS}
                  appearance={Appearance.OUTLINED}
                  leadingElement={<MdCheck />}
                  disabled={selectedCount === 0}
                  onClick={() => decide('approved', selectedKeys)}
                >
                  Approve
                </Button>
                <Button
                  design={Intent.ERROR}
                  appearance={Appearance.OUTLINED}
                  leadingElement={<MdClose />}
                  disabled={selectedCount === 0}
                  onClick={() => decide('rejected', selectedKeys)}
                >
                  Reject
                </Button>
              </div>
            </div>
          )}
          {adGroups.length === 0 && (
            <EmptyState
              title={
                filterLines.length === 0
                  ? 'No ads are ready for your review yet'
                  : 'No ads match these filters'
              }
            />
          )}
          {adGroups.map(group => {
            const groupId = groupIdOf(
              adKey(group.platform, group.accountId, group.adGroupId, group.ads[0].adId)
            );
            const actionableKeys = groupKeys(group);
            const statuses = group.ads.map(ad =>
              stageStatus(adKey(group.platform, group.accountId, group.adGroupId, ad.adId))
            );
            const isGroupDecided = statuses.every(status => status !== 'pending');
            const isGroupCollapsed = isGroupDecided !== toggledGroups.has(groupId);
            const groupName = group.adGroupName ?? `Ad group ${group.adGroupId}`;
            return (
              <section
                key={`${group.platform}|${group.accountId}|${group.campaignId}|${group.adGroupId}`}
                className='run-review-group'
              >
                <div
                  className={cn('run-review-group-header', { open: !isGroupCollapsed })}
                  {...toggleOnClickProps(
                    () => toggleExpanded(setToggledGroups, groupId),
                    !isGroupCollapsed
                  )}
                >
                  {/* Nothing to select when every ad in the group is locked. */}
                  {actionableKeys.length > 0 && (
                    <Checkbox
                      id={`run-review-group-${groupId}`}
                      aria-label={`Select ${groupName}`}
                      {...selectAllProps(actionableKeys)}
                    />
                  )}
                  <IconButton
                    className={cn('run-review-chevron', { expanded: !isGroupCollapsed })}
                    size={Size.MD}
                    design={Design.NEUTRAL}
                    appearance={Appearance.EMPTY}
                    tooltip={{ text: isGroupCollapsed ? 'Show ads' : 'Hide ads' }}
                    onClick={() => toggleExpanded(setToggledGroups, groupId)}
                  >
                    <MdExpandMore />
                  </IconButton>
                  <div className='run-review-group-title'>
                    <span className='run-review-group-ids'>
                      {`${getPlatformLabel(group.platform)} · Campaign ${
                        group.campaignName ?? group.campaignId
                      } · Account ${group.accountId}`}
                    </span>
                    <span className='run-review-group-name'>{groupName}</span>
                  </div>
                  {(['pending', 'approved', 'rejected'] as const).map(status => {
                    const count = statuses.filter(s => s === status).length;
                    return (
                      count > 0 && (
                        <Badge key={status} size={Size.SM} design={STATUS_INTENT[status]}>
                          {`${count} ${status}`}
                        </Badge>
                      )
                    );
                  })}
                  {!isGroupCollapsed && actionableKeys.length > 0 && (
                    <div className='run-review-inline-actions'>
                      <Button
                        design={Intent.SUCCESS}
                        appearance={Appearance.OUTLINED}
                        leadingElement={<MdCheck />}
                        onClick={() => decide('approved', actionableKeys)}
                      >
                        Approve all
                      </Button>
                      <Button
                        design={Intent.ERROR}
                        appearance={Appearance.OUTLINED}
                        leadingElement={<MdClose />}
                        onClick={() => decide('rejected', actionableKeys)}
                      >
                        Reject all
                      </Button>
                    </div>
                  )}
                </div>
                {!isGroupCollapsed && (
                  <div className='run-review-group-body'>
                    {group.ads.map(ad => {
                      const key = adKey(group.platform, group.accountId, group.adGroupId, ad.adId);
                      const approval = getApproval(key);
                      const isDecided = stageStatus(key) !== 'pending';
                      const isCollapsed = isDecided !== toggledAds.has(key);
                      const chevron = (
                        <IconButton
                          className={cn('run-review-chevron', { expanded: !isCollapsed })}
                          size={Size.MD}
                          design={Design.NEUTRAL}
                          appearance={Appearance.EMPTY}
                          tooltip={{ text: isCollapsed ? 'Show ad' : 'Hide ad' }}
                          onClick={() => toggleExpanded(setToggledAds, key)}
                        >
                          <MdExpandMore />
                        </IconButton>
                      );
                      const selectControl = isReadOnly(key) ? (
                        <span className='run-review-ad-id'>Ad {ad.adId}</span>
                      ) : (
                        <Checkbox
                          id={`run-review-ad-${key}`}
                          label={`Ad ${ad.adId}`}
                          checked={selected.has(key)}
                          onChange={e => toggle([key], e.target.checked)}
                        />
                      );
                      const rejectedLineCount = getLineRejections(key).length;
                      const review: LineReview = {
                        stage,
                        rejections: getVisibleLineRejections(key),
                        hiddenRejections: isInternal
                          ? []
                          : getApproval(key).lineRejections.filter(r => r.stage === 'internal'),
                        isEditable: !isReadOnly(key)
                      };
                      const keywords = getAdKeywords(ad);
                      const isKeywordsExpanded = expandedKeywords.has(key);
                      const hiddenKeywordCount = keywords.length - KEYWORD_LIMIT;
                      const visibleKeywords = isKeywordsExpanded
                        ? keywords
                        : keywords.slice(0, KEYWORD_LIMIT);
                      const keywordChip = (keyword: string) => (
                        <Badge
                          key={keyword}
                          className='run-review-keyword'
                          size={Size.SM}
                          appearance={Appearance.OUTLINED}
                          design={Design.BRAND}
                        >
                          {keyword}
                        </Badge>
                      );
                      // Keywords are internal context; clients (and ?as=client) don't see them.
                      const keywordChips = isInternal && keywords.length > 0 && (
                        <>
                          <span className='run-review-keywords-label'>Keywords</span>
                          {visibleKeywords.slice(0, -1).map(keywordChip)}
                          {/* The last chip and the toggle wrap as one, so the toggle stays beside it. */}
                          <span className='run-review-keywords-end'>
                            {keywordChip(visibleKeywords[visibleKeywords.length - 1])}
                            {hiddenKeywordCount > 0 && (
                              <Button
                                size={Size.SM}
                                appearance={Appearance.EMPTY}
                                onClick={() => toggleExpanded(setExpandedKeywords, key)}
                              >
                                {isKeywordsExpanded ? 'Show less' : `+${hiddenKeywordCount} more`}
                              </Button>
                            )}
                          </span>
                        </>
                      );
                      // Clients get the flag reasons as a quiet hover; the chip band is internal only.
                      const flaggedHint = !isInternal && ad.flagged && (
                        <InfoTooltip
                          size={Size.SM}
                          className='run-review-flagged-hint'
                          aria-label='Why this ad was flagged'
                        >
                          <strong>Why this ad was flagged</strong>
                          <div>{ad.flagged.summary}</div>
                        </InfoTooltip>
                      );
                      const diffSummary = (
                        <span className='run-review-diff-summary'>
                          {getDiffSummary(ad, review)}
                        </span>
                      );
                      const showsNote = !isReadOnly(key) || !!getAdNote(key);
                      const showsClientNote = isInternal && !!approval.clientNote;
                      if (isCollapsed) {
                        return (
                          <div
                            key={key}
                            className={cn('run-review-ad', 'collapsed', {
                              selected: selected.has(key)
                            })}
                          >
                            <div
                              className='run-review-ad-header'
                              {...toggleOnClickProps(
                                () => toggleExpanded(setToggledAds, key),
                                false
                              )}
                            >
                              {selectControl}
                              {keywordChips}
                              {flaggedHint}
                              {diffSummary}
                              {/* The note takes the free space, pushing the status and chevron right. */}
                              <span className='run-review-ad-note run-review-ad-note-snippet'>
                                {getAdNote(key)}
                              </span>
                              <Badge size={Size.SM} design={STATUS_INTENT[stageStatus(key)]}>
                                {/* Approving with some lines rejected is the common case, so the count stays visible. */}
                                {`${capitalizeString(stageStatus(key))}${
                                  rejectedLineCount > 0
                                    ? ` · ${rejectedLineCount} line${rejectedLineCount === 1 ? '' : 's'} rejected`
                                    : ''
                                }`}
                              </Badge>
                              {chevron}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={key}
                          className={cn('run-review-ad', { selected: selected.has(key) })}
                        >
                          <div
                            className='run-review-ad-header'
                            {...toggleOnClickProps(() => toggleExpanded(setToggledAds, key), true)}
                          >
                            {selectControl}
                            {/* Clients only ever see internally approved ads, so the badge says nothing to them; a
                            pending status says nothing either. */}
                            {isInternal && approval.internalStatus !== 'pending' && (
                              <Badge size={Size.SM} design={STATUS_INTENT[approval.internalStatus]}>
                                Internal: {approval.internalStatus}
                              </Badge>
                            )}
                            {/* A pending client status says nothing; show it once the client has decided. */}
                            {approval.clientStatus !== 'pending' && (
                              <Badge size={Size.SM} design={STATUS_INTENT[approval.clientStatus]}>
                                Client: {approval.clientStatus}
                              </Badge>
                            )}
                            {keywordChips}
                            {flaggedHint}
                            <div className='run-review-inline-actions'>
                              {diffSummary}
                              {!isReadOnly(key) && (
                                <>
                                  <Button
                                    design={Intent.SUCCESS}
                                    appearance={Appearance.OUTLINED}
                                    leadingElement={<MdCheck />}
                                    onClick={() => decide('approved', [key])}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    design={Intent.ERROR}
                                    appearance={Appearance.OUTLINED}
                                    leadingElement={<MdClose />}
                                    onClick={() => decide('rejected', [key])}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                              {chevron}
                            </div>
                          </div>
                          {/* Why the run picked this ad, and copy warnings: internal context only. */}
                          {isInternal &&
                            (!!ad.flagged?.reasons.length || !!ad.warnings?.length) && (
                              <div className='run-review-ad-flags'>
                                {!!ad.flagged?.reasons.length && (
                                  <div className='run-review-flagged' title={ad.flagged.summary}>
                                    <span className='run-review-keywords-label'>Why flagged</span>
                                    {ad.flagged.reasons.map(reason => (
                                      <Badge
                                        key={reason.metric}
                                        size={Size.SM}
                                        appearance={Appearance.OUTLINED}
                                        design={Design.NEUTRAL}
                                      >
                                        {formatFlagReason(reason)}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                {ad.warnings?.map(warning => (
                                  <div key={warning} className='run-review-warning'>
                                    <MdWarning />
                                    {warning}
                                  </div>
                                ))}
                              </div>
                            )}
                          <AdCopy
                            ad={ad}
                            review={review}
                            onLinesChange={(rejections, save) =>
                              linesChange.current?.(key, rejections, save)
                            }
                          />
                          {/* Footer band: the client's note (internal only) and this stage's note. */}
                          {(showsNote || showsClientNote) && (
                            <div className='run-review-ad-footer'>
                              {showsClientNote && (
                                <span className='run-review-ad-note'>
                                  Client: {approval.clientNote}
                                </span>
                              )}
                              {isReadOnly(key) ? (
                                getAdNote(key) && (
                                  <span className='run-review-ad-note'>Note: {getAdNote(key)}</span>
                                )
                              ) : (
                                <>
                                  <Form.Label
                                    className='run-review-footer-label'
                                    htmlFor={`run-review-note-${key}`}
                                  >
                                    Note
                                  </Form.Label>
                                  <Form.Control
                                    id={`run-review-note-${key}`}
                                    className='run-review-ad-note-field'
                                    placeholder='Note for this ad (optional)'
                                    value={getAdNote(key)}
                                    onChange={e =>
                                      setAdNotes(current => ({ ...current, [key]: e.target.value }))
                                    }
                                  />
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </>
      )}
      <ToastContainer position='bottom-end' containerPosition='fixed' className='p-3'>
        <Toast
          bg='danger'
          show={saveError != null}
          onClose={() => setSaveError(undefined)}
          delay={6000}
          autohide
        >
          <Toast.Body className='text-white'>{`Couldn't save: ${saveError}`}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};
